import {
  Coding,
  Extension,
  QuestionnaireItemEnableWhen,
  QuestionnaireResponseItemAnswer
} from '@medplum/fhirtypes'
import {
  QUESTIONNAIRE_DISABLE_INPUT_CONDITION_EXTENSION_URL,
  QUESTIONNAIRE_GENERATE_ANSWER_EXTENSION_URL,
  QUESTIONNAIRE_HELPER_TEXT_EXTENSION_URL
} from 'fhir/Questionnaire/constants'
import { getCodeableConceptExtensionValue } from 'fhir/utils'
import { dataFormatMappers } from '../constants'
import { findExtensionAndReturnComponentRef } from '../helpers'
import { ASSESSMENTS_STATIC_DATA } from '../PatAssessment/constants'

export const compareItemWithRules = (
  rules: QuestionnaireItemEnableWhen[],
  answers: Record<string, QuestionnaireResponseItemAnswer>
): boolean => {
  return rules?.some((rule) => {
    const { question, operator, answerCoding } = rule
    const itemValue = answers[question as string]

    if (!itemValue) {
      // If the answer for the question is not available, return false
      return false
    }

    const { code } = answerCoding as Coding
    const itemCode = itemValue['valueCoding']?.code

    switch (operator) {
      case '=':
        return itemCode === code
      case '!=':
        return itemCode !== code
      // Add more comparison operators as needed
      default:
        throw new Error(`Unsupported operator: ${operator}`)
    }
  })
}

export const extractFromReference = (
  reference: Coding,
  part: 'entity' | 'path' | 'full'
): string | null => {
  const parts = reference?.code?.split(':')
  if (!parts || parts.length < 2) return null

  switch (part) {
    case 'entity':
      return parts[1].split('.')[0]
    case 'path':
      return parts[1]
    case 'full':
      return parts.join(':')
    default:
      return null
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const extractDataFromReference = (data, reference: Coding): any => {
  if (!reference || !data) {
    return
  }
  const dataType = reference.system
  const dataRef = reference.code
  const steps = dataRef && dataRef.includes('.') ? dataRef.split('.') : null

  let value = data && dataType && data[dataType]
  if (value && dataRef) {
    // If reference and type are the same, return the whole data
    if (dataRef === dataType) {
      return data[dataType]
    }
    // If there are steps, iterate through the object keys until the last step
    if (steps) {
      if (Array.isArray(value)) {
        for (const step of steps) {
          value = value[step]
        }
        return value
      }
    }
    // if reference and type are different and if there are no steps, check if dataRef is in value and return that value
    return dataRef in value ? value[dataRef] : undefined
  }
}

type OptionsValue = {
  label: string
  value: string
  code: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getFormattedDataOptions = (item: any, format: string): OptionsValue => {
  const mapper = dataFormatMappers[format]
  return mapper ? mapper(item) : { label: 'Unknown', value: 'Unknown', code: 'Unknown' }
}

export const getSectionReferenceId = (question): string => {
  // if question's answer is dependent on another value, this is where the id pointing to the question is stored
  return question?.extension?.find((extItem) => {
    if (extItem?.url === QUESTIONNAIRE_GENERATE_ANSWER_EXTENSION_URL) {
      const codingArray = extItem?.valueCodeableConcept?.coding
      return codingArray && codingArray.length > 0
    }
  })?.valueCodeableConcept?.coding?.[0]?.code
}
export const getDisableInputCondition = (question): Record<string, string> => {
  const targetExtension = question?.extension?.find((extItem) => {
    if (extItem?.url === QUESTIONNAIRE_DISABLE_INPUT_CONDITION_EXTENSION_URL) {
      const codingArray = extItem?.valueCodeableConcept?.coding
      return codingArray && codingArray.length > 0
    }
  })

  const code = targetExtension?.valueCodeableConcept?.coding?.[0]?.code
  const display = targetExtension?.valueCodeableConcept?.coding?.[0]?.display
  return { code, display }
}

export const getHelperTextFromExtension = (extensions): string | null => {
  const helperTextExtension = getCodeableConceptExtensionValue(
    extensions,
    QUESTIONNAIRE_HELPER_TEXT_EXTENSION_URL
  )
  return helperTextExtension?.coding?.[0]?.display ?? null
}

export const renderConditionalQuestions = ({
  conditionalQuestions,
  acceptedComponentTypes,
  answers
}): JSX.Element | null => {
  const renderedQuestions: JSX.Element[] = []
  if (conditionalQuestions && conditionalQuestions.length > 0) {
    conditionalQuestions.forEach((question) => {
      const elementRef =
        question.extension &&
        findExtensionAndReturnComponentRef({
          extensions: question.extension as Extension[],
          linkId: question.linkId
        })
      let TargetElement
      if (elementRef) {
        TargetElement = acceptedComponentTypes[elementRef]
      }

      if (question.enableWhen) {
        const enableWhenRules = question.enableWhen as QuestionnaireItemEnableWhen[]
        const enable = compareItemWithRules(enableWhenRules, answers)

        if (enable && TargetElement) {
          renderedQuestions.push(
            <TargetElement key={question.linkId} question={question} conditionalQuestion />
          )
        }
      }
    })
  }
  if (renderedQuestions.length > 0) {
    return <>{renderedQuestions}</>
  } else {
    return null
  }
}

export const getStaticDataFromExtension = (assessmentType, dataReference): Coding[] => {
  const dataReferenceType = dataReference && dataReference.code
  const staticDataListByAssessment = ASSESSMENTS_STATIC_DATA[assessmentType]
  const staticData =
    dataReferenceType &&
    dataReferenceType in staticDataListByAssessment &&
    staticDataListByAssessment[dataReferenceType]

  return staticData
}
